import { PiniaColada } from "@pinia/colada";
import { createPinia } from "pinia";
import { defineCustomElement } from "vue";
import { I18nInjectionKey } from "vue-i18n";

import type { WidgetMethods } from "@/shared/config";

import { createWidgetI18n, WIDGET_TAG_NAME } from "@/shared/config";
import { appendStylesheet, logger } from "@/shared/lib";

import App from "../ui/App.vue";

const FONTS_URL = "https://fonts.googleapis.com/css2?family=Manrope:wght@400;700&display=swap";

// Every <b2b-widget> creates its own Vue app, so pinia (with the query cache) and i18n are isolated per instance
const WidgetElement = defineCustomElement(App, {
    shadowRoot: true,
    configureApp(app) {
        app.use(createPinia());
        // Must be installed after pinia: the query cache is stored in it
        app.use(PiniaColada);

        // Devtools look for pinia only during the first 15s after page load, but the widget
        // is mounted whenever the partner calls mount(), so connect them right after install
        if (import.meta.env.DEV) {
            void import("@pinia/colada-devtools/client-script").then(({ default: setup }) =>
                setup(),
            );
        }

        const i18n = createWidgetI18n();
        app.use(i18n);
        // The custom element root resolves i18n by I18nInjectionKey instead of the key app.use() provides
        app.provide(I18nInjectionKey, i18n);
    },
});

/** <b2b-widget> with the methods exposed by the root component */
export type WidgetElementInstance = InstanceType<typeof WidgetElement> & WidgetMethods;

declare global {
    interface HTMLElementTagNameMap {
        [WIDGET_TAG_NAME]: WidgetElementInstance;
    }
}

export function createWidgetElement(
    props: ConstructorParameters<typeof WidgetElement>[0],
): WidgetElementInstance {
    const registered = customElements.get(WIDGET_TAG_NAME);

    if (!registered) {
        appendStylesheet(FONTS_URL);
        customElements.define(WIDGET_TAG_NAME, WidgetElement);
    } else if (registered !== WidgetElement) {
        // Another copy of the widget script is already loaded on the page
        logger.warn(`<${WIDGET_TAG_NAME}> is already defined by another script`);
        return Object.assign(document.createElement(WIDGET_TAG_NAME), props);
    }

    return new WidgetElement(props) as WidgetElementInstance;
}
